import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorScreen, isChunkLoadError, recoverFromChunkError } from './ErrorScreen';

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
};

/** Top-level React error boundary. Catches anything that escapes a route's
 *  own `errorElement` (e.g. errors thrown inside lazy chunk loads, render
 *  errors in providers, etc) and renders the friendly ErrorScreen. */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Stale-chunk after redeploy → one-shot reload.
    if (isChunkLoadError(error) && recoverFromChunkError()) return;
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error, info);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return <ErrorScreen error={this.state.error} onRetry={this.reset} />;
    }
    return this.props.children;
  }
}
